import { css } from "@emotion/react";
import { Numbers } from "@mui/icons-material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ImageIcon from "@mui/icons-material/Image";
import LanguageIcon from "@mui/icons-material/Language";
import MapIcon from "@mui/icons-material/Map";
import NotesIcon from "@mui/icons-material/Notes";
import ShortTextIcon from "@mui/icons-material/ShortText";
import { Theme, useTheme } from "@mui/material";
import { CalendarIcon } from "@mui/x-date-pickers";
import { match } from "ts-pattern";

import {
  Question,
  ResponseAnswer,
} from "~/components/database/detail/database-content/types";
import InputArea from "~/components/database/detail/response-modal-content/response-form/InputArea";
import { fixedWidth } from "~/lib/client/design";
import { engFont } from "~/lib/client/fonts";
import useIsSpMode, { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import { AnswerTypeEnum } from "~/lib/graphql/client";
import { AnswerInput as ResponseAnswerInput } from "~/lib/graphql/client";

export type AnswerFormProps = {
  question: Question;
  updateResponseAnswer: (answer: ResponseAnswer) => void;
  setIsValid: (isValid: boolean) => void;
  responseAnswerInput: ResponseAnswerInput;
};

export type Coordinate = {
  latitude: number;
  longitude: number;
};

export const Icon = ({ answerType }: { answerType: Question["answerType"] }) =>
  match(answerType)
    .with(AnswerTypeEnum.Text, () => <ShortTextIcon />)
    .with(AnswerTypeEnum.ShortText, () => <ShortTextIcon />)
    .with(AnswerTypeEnum.LongText, () => <NotesIcon />)
    .with(AnswerTypeEnum.Number, () => <Numbers />)
    .with(AnswerTypeEnum.Location, () => <MapIcon />)
    .with(AnswerTypeEnum.Date, () => <CalendarIcon />)
    .with(AnswerTypeEnum.Reference, () => <AddLinkIcon />)
    .with(AnswerTypeEnum.ImageUrl, () => <ImageIcon />)
    .with(AnswerTypeEnum.Url, () => <LanguageIcon />)
    .exhaustive();

const AnswerForm = (props: AnswerFormProps) => {
  const theme = useTheme();
  const isSpMode = useIsSpMode();

  if (isSpMode === undefined) return null;

  return isSpMode ? (
    <div css={spRootStyle(theme)}>
      <p className={engFont.className} css={questionContainerStyle(theme)}>
        Q{props.question.position + 1}
      </p>
      <div css={rightContainerStyle(theme)}>
        <p css={questionStyle(theme)}>
          <Icon answerType={props.question.answerType} />
          {props.question.title}
        </p>
        {props.question.description && (
          <p css={questionLabelStyle(theme)}>{props.question.description}</p>
        )}
        <div css={inputAreaStyle(theme)}>
          <InputArea {...props} />
        </div>
      </div>
    </div>
  ) : (
    <div css={rootStyle(theme)}>
      <p className={engFont.className} css={questionContainerStyle(theme)}>
        Q{props.question.position + 1}
      </p>
      <div css={rightContainerStyle(theme)}>
        <p css={questionStyle(theme)}>
          <Icon answerType={props.question.answerType} />
          {props.question.title}
        </p>
        <div css={labelAndInputAreaStyle(theme)}>
          <p css={questionLabelStyle(theme)}>{props.question.description}</p>
          <div css={inputAreaStyle(theme)}>
            <InputArea {...props} />
          </div>
        </div>
      </div>
    </div>
  );
};

const rootStyle = (theme: Theme) => css`
  ${fixedWidth("40rem")}
  border-bottom: 1px solid ${theme.palette.divider};
  display: flex;
  flex-direction: row;
  width: 100%;

  p {
    margin: 0;
  }
`;

const spRootStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid ${theme.palette.divider};
`;

const questionContainerStyle = (theme: Theme) => css`
  padding: 1rem;
  border-right: 1px solid ${theme.palette.divider};
  color: ${theme.palette.text.primary};
`;

const rightContainerStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  width: 100%;

  p {
    margin: 0;
  }

  @media (max-width: ${SP_MODE_WIDTH}px) {
    flex: 1;
  }
`;

const questionStyle = (theme: Theme) => css`
  color: ${theme.palette.primary.main};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border-bottom: 1px solid ${theme.palette.divider};

  @media (max-width: ${SP_MODE_WIDTH}px) {
    border-bottom: none;
  }
`;

const labelAndInputAreaStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: row;
`;

const questionLabelStyle = (theme: Theme) => css`
  font-size: 12pt;
  margin: 0 0.5rem;
  color: ${theme.palette.text.secondary};
  ${fixedWidth("12.5rem")}
  padding: 0.5rem;
  border-right: 1px solid ${theme.palette.divider};

  @media (max-width: ${SP_MODE_WIDTH}px) {
    width: 100%;
    border-right: none;
    min-width: 0;
    max-width: 100%;
  }
`;

const inputAreaStyle = (theme: Theme) => css`
  padding: 0.5rem;
  width: 100%;
`;

export default AnswerForm;
